import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useCurrentProfile } from '@/features/auth/api';
import { roomsKeys, setSelfConnected } from './api';
/**
 * Branche un client à un canal room :
 *  - Track sa présence (qui est en ligne)
 *  - Écoute les Postgres Changes sur `rooms` et `room_players` filtrés par
 *    room_id, et invalide la cache TanStack Query → l'UI se met à jour seule.
 *  - Met à jour `room_players.is_connected` au mount/unmount.
 */
export function useRoomChannel(params) {
    const { roomId, code } = params;
    const { user } = useAuth();
    const { data: profile } = useCurrentProfile();
    const qc = useQueryClient();
    const [state, setState] = useState({ presence: {}, ready: false });
    useEffect(() => {
        if (!code || !roomId || !user?.id || !profile)
            return;
        const channel = supabase.channel(`room:${code}`, {
            config: { presence: { key: user.id } },
        });
        // Toutes les `.on()` DOIVENT être appelées AVANT `.subscribe()`. Sinon
        // Supabase lève "cannot add callbacks after subscribe()". On les déclare
        // ici en chaîne, puis on subscribe une seule fois en bas.
        channel
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'rooms', filter: `id=eq.${roomId}` }, () => {
            void qc.invalidateQueries({ queryKey: roomsKeys.byId(roomId) });
        })
            .on('postgres_changes', { event: '*', schema: 'public', table: 'room_players', filter: `room_id=eq.${roomId}` }, () => {
            void qc.invalidateQueries({ queryKey: roomsKeys.members(roomId) });
        })
            .on('postgres_changes', { event: '*', schema: 'public', table: 'games', filter: `room_id=eq.${roomId}` }, (payload) => {
            void qc.invalidateQueries({ queryKey: roomsKeys.activeGame(roomId) });
            const newRow = payload?.new;
            if (newRow?.id) {
                void qc.invalidateQueries({ queryKey: ['games', 'by-id', newRow.id] });
            }
        })
            .on('presence', { event: 'sync' }, () => {
            const raw = channel.presenceState();
            const flat = {};
            for (const [playerId, metas] of Object.entries(raw)) {
                const m = metas[0];
                if (m)
                    flat[playerId] = m;
            }
            setState((s) => ({ ...s, presence: flat }));
        });
        let cancelled = false;
        void channel.subscribe(async (status) => {
            if (cancelled)
                return;
            if (import.meta.env.DEV) {
                // eslint-disable-next-line no-console
                console.debug(`[useRoomChannel] room:${code} → ${status}`);
            }
            if (status === 'SUBSCRIBED') {
                try {
                    await channel.track({
                        player_id: user.id,
                        pseudo: profile.pseudo,
                        avatar_url: profile.avatar_url,
                        joined_at: new Date().toISOString(),
                    });
                    await setSelfConnected(roomId, user.id, true);
                    if (!cancelled)
                        setState((s) => ({ ...s, ready: true }));
                }
                catch {
                    /* best-effort */
                }
            }
        });
        // Cleanup CRITIQUE : `supabase.channel(name)` réutilise un canal existant
        // si le nom est déjà dans le registre interne. En StrictMode (dev), le
        // double-mount appelait removeChannel dans une closure async → quand le
        // 2e mount tournait, l'ancien canal était encore là → réutilisé →
        // .on() après .subscribe() → erreur. removeChannel synchrone retire la
        // ligne du registre immédiatement, le tear-down réseau finit en async.
        return () => {
            cancelled = true;
            channel.untrack().catch(() => { });
            setSelfConnected(roomId, user.id, false).catch(() => { });
            void supabase.removeChannel(channel);
        };
    }, [code, roomId, user?.id, profile?.pseudo, profile?.avatar_url, qc]);
    return state;
}
